import { useEffect, useState, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { Plus, FileDown } from "lucide-react"
import { SearchFilterBar } from "@/modules/shared/ui/components/custom/SearchFilterBar"
import { toast } from "sonner"
import { Button } from "@/modules/shared/ui/components/button"
import { Card, CardContent } from "@/modules/shared/ui/components/card"
import { Spinner } from "@/modules/shared/ui/components/spinner"
import { AddEditEmployeeDialog } from "@/modules/onboarding/ui/components/AddEditEmployeeDialog"
import { EmployeeCard } from "@/modules/onboarding/ui/components/EmployeeCard"
import { ExportToSageVipDialog } from "@/modules/onboarding/ui/components/ExportToSageVipDialog"
import { EmployeesEmpty } from "@/modules/onboarding/ui/components/EmployeesEmpty"
import { EmployeesError } from "@/modules/onboarding/ui/components/EmployeesError"
import {
  EmployeeDto,
  toEmployeeStatusLabel,
} from "@/modules/onboarding/employee/dtos"
import { useEmployeeService } from "@/modules/onboarding/employee/hooks/use-employee"
import { useSelector, useDispatch } from "react-redux"
import { selectCurrentOrganization } from "@/modules/organizations/store/organizationSlice"
import { apiClient } from "@/lib/api-client"
import { useVacancies } from "@/modules/recruitment/hooks/useVacancies"
import { AppDispatch } from "@/store"
import { fetchEmployeesThunk } from "@/modules/onboarding/employee/store/employeeSlice"
import { PlanType } from "@/modules/organizations/types/plan-type"

const STATUSES = [
  { label: "All Statuses", value: "all" },
  { label: "Pending", value: "Pending" },
  { label: "Started", value: "Started" },
  { label: "Submitted", value: "Submitted" },
  { label: "Approved", value: "Approved" },
  { label: "Rejected", value: "Rejected" },
  { label: "Completed", value: "Completed" },
  { label: "Offboarding", value: "Offboarding" },
  { label: "Terminated", value: "Terminated" },
]

export default function EmployeesPage() {
  const [searchQuery, setSearchQuery] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [isAddOpen, setIsAddOpen] = useState(false)
  const [isVipDialogOpen, setIsVipDialogOpen] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [hasSageVip, setHasSageVip] = useState(false)
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const currentOrg = useSelector(selectCurrentOrganization)
  const hasRecruitment =
    currentOrg?.plan === PlanType.Recruitment ||
    currentOrg?.plan === PlanType.FullHiring

  const { vacancies } = useVacancies()
  const { employees, isLoading, error, createEmployee, inviteEmployee } =
    useEmployeeService()
  const [invitingEmployeeId, setInvitingEmployeeId] = useState<string | null>(
    null,
  )

  const formId = "add-employee-form"

  const handleCreateEmployee = async (formData: FormData) => {
    setIsCreating(true)
    try {
      await createEmployee(formData)
      setIsAddOpen(false)
      toast.success("Employee created successfully")
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Failed to create employee"
      toast.error(message)
    } finally {
      setIsCreating(false)
      setIsAddOpen(false)
    }
  }

  useEffect(() => {
    const term = searchQuery.trim() || undefined
    const status = statusFilter === "all" ? undefined : statusFilter
    dispatch(
      fetchEmployeesThunk({ searchTerm: term, onboardingStatus: status }),
    )
  }, [searchQuery, statusFilter, dispatch])

  useEffect(() => {
    if (!currentOrg?.id) return
    apiClient
      .get(`/organizations/${currentOrg.id}/sagevip/connection`)
      .then(() => setHasSageVip(true))
      .catch(() => setHasSageVip(false))
  }, [currentOrg?.id])

  const handleViewEmployee = (employee: EmployeeDto) => {
    navigate(`/onboarding/employees/${employee.id}`)
  }

  const handleInviteEmployee = useCallback(
    async (employee: EmployeeDto) => {
      setInvitingEmployeeId(employee.id)
      try {
        await inviteEmployee(employee.id)
        toast.success(
          `Invite sent to ${employee.firstName} ${employee.lastName}`,
        )
      } catch {
        toast.error(
          `Failed to send invite to ${employee.firstName} ${employee.lastName}`,
        )
      } finally {
        setInvitingEmployeeId(null)
      }
    },
    [inviteEmployee],
  )

  const refreshEmployees = () =>
    dispatch(
      fetchEmployeesThunk({
        searchTerm: searchQuery.trim() || undefined,
        onboardingStatus: statusFilter === "all" ? undefined : statusFilter,
      }),
    )

  return (
    <div className="space-y-6 p-6">
      <Card className="bg-card border border-border">
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground">
                All Employees
              </h1>
              <p className="text-muted-foreground mt-1">
                Search, add and manage employees in your organisation
              </p>
            </div>

            <div className="flex items-center gap-2">
              {hasSageVip && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsVipDialogOpen(true)}
                  className="text-green-600 border-green-200 hover:bg-green-50 hover:border-green-300"
                >
                  <FileDown className="w-4 h-4 mr-2" />
                  Export to Sage VIP
                </Button>
              )}
              <Button
                variant="ghost"
                className="w-fit pl-0 hover:text-blue-600 text-muted-foreground transition-all duration-200"
                onClick={() => setIsAddOpen(true)}
                disabled={isCreating}
              >
                <Plus className="w-4 h-4" />
                Add Employee
              </Button>
            </div>
          </div>

          <SearchFilterBar
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder="Search by name or email..."
            filterValue={statusFilter}
            onFilterChange={setStatusFilter}
            filterOptions={STATUSES}
            filterPlaceholder="All Statuses"
            hasData={employees.length > 0}
          />
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner className="size-6" />
        </div>
      ) : error ? (
        <EmployeesError />
      ) : employees.length > 0 ? (
        <div className="grid grid-cols-1 gap-4">
          {employees.map((employee: EmployeeDto) => (
            <EmployeeCard
              key={employee.id}
              employee={employee}
              onView={handleViewEmployee}
              onInvite={handleInviteEmployee}
              isInviting={invitingEmployeeId === employee.id}
              hasSageVip={hasSageVip}
              onOffboardingStarted={refreshEmployees}
            />
          ))}
        </div>
      ) : (
        <EmployeesEmpty onAddEmployee={() => setIsAddOpen(true)} />
      )}

      {hasSageVip && (
        <ExportToSageVipDialog
          open={isVipDialogOpen}
          onOpenChange={setIsVipDialogOpen}
          employees={employees.filter(
            (e) => toEmployeeStatusLabel(e.status) === "Active",
          )}
        />
      )}

      <AddEditEmployeeDialog
        open={isAddOpen}
        onOpenChange={setIsAddOpen}
        formId={formId}
        submitLabel="Create Employee"
        isSubmitting={isCreating}
        availableJobs={
          hasRecruitment
            ? vacancies.map((v: { id: string; title: string }) => ({
              id: v.id,
              title: v.title,
            }))
            : undefined
        }
        onSubmit={handleCreateEmployee}
      />
    </div>
  )
}
